/**
 * ProcedureInitiationPrint.js
 * Dedicated A4 print view — pure table-based B&W layout.
 * Hidden off-screen on screen; rendered into react-to-print iframe on print.
 */
import React from 'react';
import PropTypes from 'prop-types';
import irmHeader from 'assets/images/irm-header.png';
import PatientCodeBarcodes from 'components/PatientCodeBarcodes';

// ─── Inline style tokens ─────────────────────────────────────────────────────
const S = {
  wrap:     { fontFamily: "'Arial', Helvetica, sans-serif", fontSize: '11px', color: '#000', lineHeight: 1.4, backgroundColor: '#fff' },
  img:      { width: '100%', display: 'block', marginBottom: '8px' },
  title:    { textAlign: 'center', fontSize: '14px', fontWeight: 'bold', borderTop: '2px solid #000', borderBottom: '2px solid #000', padding: '5px 0', margin: '6px 0 8px', textTransform: 'uppercase', letterSpacing: '1px' },
  meta:     { display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#444', marginBottom: '8px' },
  secHead:  { color: '#fff', fontWeight: 'bold', fontSize: '12px', padding: '5px 10px', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '14px', display: 'block' },
  subHead:  { background: '#555', color: '#fff', fontWeight: 'bold', fontSize: '10px', padding: '4px 10px', textTransform: 'uppercase', letterSpacing: '0.3px', marginTop: '8px', display: 'block' },
  miniHead: { background: '#888', color: '#fff', fontWeight: 'bold', fontSize: '9px', padding: '3px 10px', textTransform: 'uppercase', marginTop: '5px', display: 'block' },
  table:    { width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginTop: '0' },
  th:       { background: '#e0e0e0', fontWeight: 'bold', fontSize: '10px', padding: '5px 7px', border: '1px solid #bbb', textAlign: 'left', color: '#000' },
  td:       { padding: '5px 7px', border: '1px solid #ccc', color: '#000', verticalAlign: 'top', fontSize: '11px' },
  lbl:      { background: '#f0f0f0', fontWeight: 'bold', fontSize: '10px', padding: '5px 7px', border: '1px solid #ccc', color: '#000', verticalAlign: 'top' },
  val:      { padding: '5px 7px', border: '1px solid #ccc', color: '#000', fontSize: '11px', verticalAlign: 'top' },
  noDataTd: { padding: '8px 10px', fontStyle: 'italic', color: '#888', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center' },
  footer:   { marginTop: '18px', borderTop: '2px solid #000', paddingTop: '8px', textAlign: 'center', fontSize: '10px', color: '#000', lineHeight: 1.7 },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return String(d); }
};
const fv = (x) => (x !== null && x !== undefined && x !== '') ? x : '—';
const today = () => new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

// Calculate age from date of birth
const calcAge = (dob) => {
  if (!dob) return null;
  try {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    return age > 0 ? age : null;
  } catch {
    return null;
  }
};

// Get contact info from donor object
const getDonorContact = (donorInfo) => {
  if (!donorInfo) return '—';
  // Try email first
  if (donorInfo?.email_address) return donorInfo.email_address;
  // Try primary phone
  if (donorInfo?.phones && Array.isArray(donorInfo.phones) && donorInfo.phones.length > 0) {
    const phone = donorInfo.phones[0];
    return phone.phone_number ? `${phone.country_code || ''}${phone.phone_number}` : '—';
  }
  // Try phone_number directly
  if (donorInfo?.phone_number) return donorInfo.phone_number;
  return '—';
};

const INFERTILITY_MAP = {
  primary:       'Primary Infertility',
  secondary:     'Secondary Infertility',
  boh:           'BOH',
  gynecological: 'Gynecological Problems',
};

const GENERIC_FIELDS = [
  { key: 'patient_history',                label: 'Personal History' },
  { key: 'family_history',                 label: 'Family History' },
  { key: 'allergic_to_drugs',              label: 'Allergic to Drugs' },
  { key: 'drug_medical_history',           label: 'Drug / Medical History' },
  { key: 'previous_infertility_treatment', label: 'Prev. Infertility Treatment' },
  { key: 'notes',                          label: 'Notes' },
];

const BLOOD_SLUGS = {
  hormonal:     'blood-investigation-params-hormonal',
  serology:     'blood-investigation-params-serology',
  biochemistry: 'blood-investigation-params-bio-chemistry',
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const SecHead = ({ children, bg }) => (
  <div style={{ ...S.secHead, background: bg }}>{children}</div>
);
SecHead.propTypes = { children: PropTypes.node.isRequired, bg: PropTypes.string };
SecHead.defaultProps = { bg: '#1a1a1a' };

const SubHead = ({ children }) => <div style={S.subHead}>{children}</div>;
SubHead.propTypes = { children: PropTypes.node.isRequired };

const MiniHead = ({ children }) => <div style={S.miniHead}>{children}</div>;
MiniHead.propTypes = { children: PropTypes.node.isRequired };

const NoDataRow = ({ cols }) => (
  <tr><td colSpan={cols} style={S.noDataTd}>— No data available —</td></tr>
);
NoDataRow.propTypes = { cols: PropTypes.number };
NoDataRow.defaultProps = { cols: 1 };

// Blood-test entries table
const BloodTable = ({ entries }) => {
  if (!entries?.length) return <table style={S.table}><tbody><NoDataRow cols={4} /></tbody></table>;
  return (
    <table style={S.table}>
      <thead>
        <tr>
          <th style={{ ...S.th, width: '15%' }}>Date</th>
          <th style={{ ...S.th, width: '32%' }}>Test</th>
          <th style={{ ...S.th, width: '20%' }}>Value</th>
          <th style={S.th}>Remarks</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((h, i) => (
          <tr key={h.id || i}>
            <td style={S.td}>{fmt(h.history_date)}</td>
            <td style={S.td}>{fv(h.name || h.param_group_member_option?.value)}</td>
            <td style={S.td} dangerouslySetInnerHTML={{ __html: h.value || '—' }} />
            <td style={S.td} dangerouslySetInnerHTML={{ __html: h.remarks || '—' }} />
          </tr>
        ))}
      </tbody>
    </table>
  );
};
BloodTable.propTypes = { entries: PropTypes.array };
BloodTable.defaultProps = { entries: [] };

// Vitals name-value table
const VitalsTable = ({ vitals }) => {
  if (!vitals) return <table style={S.table}><tbody><NoDataRow cols={4} /></tbody></table>;
  const { weight, height, bmi, bp_systolic, bp_diastolic, pulse } = vitals;
  const hasAny = weight || height || bmi || bp_systolic || pulse;
  if (!hasAny) return <table style={S.table}><tbody><NoDataRow cols={4} /></tbody></table>;
  return (
    <table style={S.table}>
      <tbody>
        <tr>
          <td style={{ ...S.lbl, width: '22%' }}>Weight</td>
          <td style={{ ...S.val, width: '28%' }}>{weight ? `${weight} kg` : '—'}</td>
          <td style={{ ...S.lbl, width: '22%' }}>Height</td>
          <td style={{ ...S.val, width: '28%' }}>{height ? `${height} cm` : '—'}</td>
        </tr>
        <tr>
          <td style={S.lbl}>BMI</td>
          <td style={S.val}>{fv(bmi)}</td>
          <td style={S.lbl}>Blood Pressure</td>
          <td style={S.val}>{bp_systolic && bp_diastolic ? `${bp_systolic} / ${bp_diastolic} mmHg` : '—'}</td>
        </tr>
        <tr>
          <td style={S.lbl}>Pulse</td>
          <td style={S.val}>{pulse ? `${pulse} bpm` : '—'}</td>
          <td style={S.lbl}></td>
          <td style={S.val}></td>
        </tr>
      </tbody>
    </table>
  );
};
VitalsTable.propTypes = {
  vitals: PropTypes.shape({
    weight:       PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height:       PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bmi:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bp_systolic:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bp_diastolic: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pulse:        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};
VitalsTable.defaultProps = { vitals: null };

// ─── Donor Info Block ─────────────────────────────────────────────────────────
const DonorInfoTable = ({ donorInfo, donorType = 'F' }) => {
  if (!donorInfo) return null;
  const genderVal = donorInfo?.gender === 'M' ? 'Male' : donorInfo?.gender === 'F' ? 'Female' : '—';
  const donorFullName = [donorInfo?.first_name, donorInfo?.middle_name, donorInfo?.last_name]
    .filter(Boolean)
    .join(' ') || donorInfo?.full_name || '—';
  // Get age from object or calculate from DOB
  const age = donorInfo?.age || calcAge(donorInfo?.date_of_birth);
  const ageStr = age ? `${age} yrs` : '—';
  const contactInfo = getDonorContact(donorInfo);
  
  return (
    <table style={S.table}>
      <tbody>
        <tr>
          <td style={{ ...S.lbl, width: '20%' }}>Donor ID</td>
          <td style={{ ...S.val, width: '30%' }}>{fv(donorInfo?.external_code)}</td>
          <td style={{ ...S.lbl, width: '20%' }}>Donor Name</td>
          <td style={{ ...S.val, width: '30%' }}><strong>{fv(donorFullName)}</strong></td>
        </tr>
        <tr>
          <td style={S.lbl}>Date of Birth</td>
          <td style={S.val}>{fmt(donorInfo?.date_of_birth)}</td>
          <td style={S.lbl}>Age</td>
          <td style={S.val}>{fv(ageStr)}</td>
        </tr>
        <tr>
          <td style={S.lbl}>Gender</td>
          <td style={S.val}>{genderVal}</td>
          <td style={S.lbl}>Contact</td>
          <td style={S.val}>{fv(contactInfo)}</td>
        </tr>
      </tbody>
    </table>
  );
};
DonorInfoTable.propTypes = {
  donorInfo: PropTypes.object,
  donorType: PropTypes.string,
};
DonorInfoTable.defaultProps = { donorInfo: null, donorType: 'F' };

// Generic + Surgical history block
const MedicalBlock = ({ genericHistory, surgicalHistory }) => {
  const hasGeneric = genericHistory && GENERIC_FIELDS.some(f => genericHistory[f.key]?.trim?.());
  const hasSurgical = surgicalHistory?.length > 0;
  if (!hasGeneric && !hasSurgical) return null;
  return (
    <>
      {hasGeneric && (
        <>
          <SubHead>Personal &amp; Medical History</SubHead>
          <table style={S.table}>
            <tbody>
              {GENERIC_FIELDS.map(f => {
                const val = genericHistory[f.key];
                if (!val?.trim?.()) return null;
                return (
                  <tr key={f.key}>
                    <td style={{ ...S.lbl, width: '30%' }}>{f.label}</td>
                    <td style={{ ...S.td }} dangerouslySetInnerHTML={{ __html: val }} />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      {hasSurgical && (
        <>
          <SubHead>Surgical History</SubHead>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: '18%' }}>Date</th>
                <th style={{ ...S.th, width: '35%' }}>Surgery</th>
                <th style={S.th}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {surgicalHistory.map((s, i) => (
                <tr key={s.id || i}>
                  <td style={S.td}>{fmt(s.history_date)}</td>
                  <td style={S.td}>{fv(s.surgery)}</td>
                  <td style={S.td} dangerouslySetInnerHTML={{ __html: s.notes || '—' }} />
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};
MedicalBlock.propTypes = {
  genericHistory:  PropTypes.object,
  surgicalHistory: PropTypes.array,
};
MedicalBlock.defaultProps = { genericHistory: null, surgicalHistory: [] };

// Categorise wife blood tests using bloodParams for slug lookup
const catWife = (histories, bloodParams) => {
  const out = { hormonal: [], serology: [], biochemistry: [] };
  if (!histories?.length || !bloodParams?.param_group_members) return out;
  histories.forEach(h => {
    const m = bloodParams.param_group_members.find(pm => pm.id === h.irm_param_group_members_id);
    if (!m) return;
    if (m.slug === BLOOD_SLUGS.hormonal)     out.hormonal.push(h);
    else if (m.slug === BLOOD_SLUGS.serology)     out.serology.push(h);
    else if (m.slug === BLOOD_SLUGS.biochemistry) out.biochemistry.push(h);
  });
  return out;
};

// Categorise husband blood tests using param_group_member.slug directly
const catHusband = (histories) => {
  const out = { hormonal: [], serology: [], biochemistry: [] };
  (histories || []).forEach(h => {
    const slug = h.param_group_member?.slug || '';
    if (slug === BLOOD_SLUGS.hormonal)      out.hormonal.push(h);
    else if (slug === BLOOD_SLUGS.serology)      out.serology.push(h);
    else if (slug === BLOOD_SLUGS.biochemistry)  out.biochemistry.push(h);
  });
  return out;
};

const BLOOD_SECTIONS = [
  { key: 'hormonal',     label: 'Hormonal' },
  { key: 'serology',     label: 'Serology' },
  { key: 'biochemistry', label: 'Bio Chemistry' },
];

const BloodSections = ({ categorized }) => {
  const hasAny = BLOOD_SECTIONS.some(s => categorized[s.key]?.length > 0);
  if (!hasAny) return <table style={S.table}><tbody><NoDataRow cols={4} /></tbody></table>;
  return (
    <>
      {BLOOD_SECTIONS.map(({ key, label }) =>
        categorized[key]?.length > 0 ? (
          <React.Fragment key={key}>
            <MiniHead>{label}</MiniHead>
            <BloodTable entries={categorized[key]} />
          </React.Fragment>
        ) : null
      )}
    </>
  );
};
BloodSections.propTypes = {
  categorized: PropTypes.shape({
    hormonal:     PropTypes.array,
    serology:     PropTypes.array,
    biochemistry: PropTypes.array,
  }),
};
BloodSections.defaultProps = { categorized: { hormonal: [], serology: [], biochemistry: [] } };

// ─── Main print component ─────────────────────────────────────────────────────
const ProcedureInitiationPrint = React.forwardRef(({
  patientInfo,
  spouseInfo,
  procedureData,
  obstetricHistory,
  miscarriageHistories,
  menstrualHistories,
  genericHistory,
  surgicalHistory,
  bloodGroup,
  procedureDetail,
  husbandGenericHistory,
  husbandSurgicalHistory,
  husbandSemenAnalysis,
  husbandContactHistories,
  storedProcedureHistories,
  bloodParams,
  storedProcedureUsgs,
  wifeVitals,
  husbandVitals,
  donorFemaleInfo,
  donorFemaleGenericHistory,
  donorFemaleSurgicalHistory,
  donorFemaleContactHistories,
  donorMaleInfo,
  donorMaleGenericHistory,
  donorMaleSurgicalHistory,
  donorMaleContactHistories,
  donorMaleSemenAnalysis,
}, ref) => {

  const wifeBlood    = catWife(storedProcedureHistories, bloodParams);
  const husbandBlood = catHusband(husbandContactHistories);
  const donorFemaleBlood = catHusband(donorFemaleContactHistories);
  const donorMaleBlood = catHusband(donorMaleContactHistories);

  // Group semen by date (latest first)
  const semenByDate = {};
  (husbandSemenAnalysis || []).forEach(h => {
    const d = h.history_date || 'Unknown';
    if (!semenByDate[d]) semenByDate[d] = [];
    semenByDate[d].push(h);
  });
  const semenDates = Object.keys(semenByDate).sort((a, b) => new Date(b) - new Date(a));

  const infertilityType = obstetricHistory?.infetrtility_type;
  const hasObstetric = !!obstetricHistory || miscarriageHistories?.length > 0;
  const hasMenstrual = menstrualHistories?.length > 0;
  const hasUsgs      = storedProcedureUsgs?.length > 0;
  const hasHusband   = !!(spouseInfo?.id);
  const hasPlan      = !!(procedureDetail?.plan || procedureDetail?.cause_of_infertility);

  return (
    <div ref={ref} style={S.wrap}>

      {/* ─── IRM Header ──────────────────────────── */}
      <img src={irmHeader} alt="IRM Header" style={S.img} />

      {/* ─── Document Title ──────────────────────── */}
      <div style={S.title}>
        Initiation of {procedureData?.procedure_type || 'IVF'} Cycle
      </div>

      <div style={S.meta}>
        <span>Procedure No: <strong>{fv(procedureData?.external_code)}</strong></span>
        <span>Date: <strong>{today()}</strong></span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
        <PatientCodeBarcodes
          mono
          patientCode={patientInfo?.external_code}
          coupleCode={patientInfo?.couple_code}
          spouseCode={hasHusband ? spouseInfo?.external_code : undefined}
          documentCode={procedureData?.external_code}
          documentLabel="Procedure No"
        />
      </div>

      {/* ─── Patient & Couple Information ─────────── */}
      <table style={S.table}>
        <tbody>
          <tr>
            <td style={{ ...S.lbl, width: '20%' }}>Patient ID</td>
            <td style={{ ...S.val, width: '30%' }}>{fv(patientInfo?.external_code)}</td>
            <td style={{ ...S.lbl, width: '20%' }}>Couple Code</td>
            <td style={{ ...S.val, width: '30%' }}>{fv(patientInfo?.couple_code)}</td>
          </tr>
          <tr>
            <td style={S.lbl}>Patient Name</td>
            <td style={S.val}><strong>{fv(patientInfo?.full_name)}</strong></td>
            <td style={S.lbl}>Date of Birth</td>
            <td style={S.val}>{fmt(patientInfo?.date_of_birth)}</td>
          </tr>
          <tr>
            <td style={S.lbl}>Age</td>
            <td style={S.val}>{fv(patientInfo?.age)}</td>
            <td style={S.lbl}>Contact</td>
            <td style={S.val}>{fv(patientInfo?.phone_number || patientInfo?.email_address)}</td>
          </tr>
          {hasHusband && (
            <tr>
              <td style={S.lbl}>Spouse Name</td>
              <td style={S.val}><strong>{fv(spouseInfo?.full_name)}</strong></td>
              <td style={S.lbl}>Spouse ID</td>
              <td style={S.val}>{fv(spouseInfo?.external_code)}</td>
            </tr>
          )}
          <tr>
            <td style={S.lbl}>Married For</td>
            <td style={S.val}>{fv(patientInfo?.married_for)}</td>
            <td style={S.lbl}>Trying For</td>
            <td style={S.val}>{fv(patientInfo?.trying_for)}</td>
          </tr>
          <tr>
            <td style={S.lbl}>Infertility Type</td>
            <td style={S.val}>
              {infertilityType ? (INFERTILITY_MAP[infertilityType.toLowerCase()] || infertilityType) : '—'}
            </td>
            <td style={S.lbl}>Blood Group</td>
            <td style={S.val}>{fv(bloodGroup)}</td>
          </tr>
        </tbody>
      </table>

      {/* ══════════════ WIFE ══════════════ */}
      <SecHead bg="#1a1a1a">♀ &nbsp;Wife — {fv(patientInfo?.full_name)}</SecHead>

      <SubHead>Vitals — Initiation Day</SubHead>
      <VitalsTable vitals={wifeVitals} />

      <MedicalBlock genericHistory={genericHistory} surgicalHistory={surgicalHistory} />

      {/* Obstetric History */}
      {hasObstetric && (
        <>
          <SubHead>
            Obstetric History
            {obstetricHistory ? ` — P${obstetricHistory.para_p1 || 0}+${obstetricHistory.para_p2 || 0}` : ''}
          </SubHead>
          {miscarriageHistories?.length > 0 ? (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: '10%' }}>Para</th>
                  <th style={{ ...S.th, width: '10%' }}>Weeks</th>
                  <th style={{ ...S.th, width: '22%' }}>Type</th>
                  <th style={{ ...S.th, width: '8%' }}>D&amp;E</th>
                  <th style={{ ...S.th, width: '10%' }}>Year</th>
                  <th style={S.th}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {miscarriageHistories.map((m, i) => (
                  <tr key={m.id || i}>
                    <td style={S.td}>{fv(m.para_number)}</td>
                    <td style={S.td}>{fv(m.weeks_of_miscarriage)}</td>
                    <td style={S.td}>{fv(m.param_group_member_option?.value || m.type_of_miscarriage)}</td>
                    <td style={S.td}>{m.de_done === true || m.de_done === 'true' ? 'Yes' : 'No'}</td>
                    <td style={S.td}>{fv(m.year_of_miscarriage)}</td>
                    <td style={S.td} dangerouslySetInnerHTML={{ __html: m.remarks || '—' }} />
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table style={S.table}><tbody><NoDataRow cols={6} /></tbody></table>
          )}
        </>
      )}

      {/* Menstrual History */}
      {hasMenstrual && (() => {
        const latest = menstrualHistories[menstrualHistories.length - 1];
        return (
          <>
            <SubHead>Latest Menstrual History</SubHead>
            <table style={S.table}>
              <tbody>
                <tr>
                  <td style={{ ...S.lbl, width: '20%' }}>LMP</td>
                  <td style={{ ...S.val, width: '30%' }}>{latest.lmp ? fmt(latest.lmp) : '—'}</td>
                  <td style={{ ...S.lbl, width: '20%' }}>Menarche</td>
                  <td style={{ ...S.val, width: '30%' }}>{fv(latest.menarche)}</td>
                </tr>
                <tr>
                  <td style={S.lbl}>Cycle Length</td>
                  <td style={S.val}>{fv(latest.cycleLength)}</td>
                  <td style={S.lbl}>Duration</td>
                  <td style={S.val}>{fv(latest.duration)}</td>
                </tr>
                <tr>
                  <td style={S.lbl}>Flow</td>
                  <td style={S.val}>{fv(latest.flow)}</td>
                  <td style={S.lbl}>Pain</td>
                  <td style={S.val}>{fv(latest.pain)}</td>
                </tr>
                {latest.other && (
                  <tr>
                    <td style={S.lbl}>Remarks</td>
                    <td colSpan={3} style={S.td} dangerouslySetInnerHTML={{ __html: latest.other }} />
                  </tr>
                )}
              </tbody>
            </table>
          </>
        );
      })()}

      {/* Wife Blood Tests */}
      <SubHead>Blood Tests</SubHead>
      <BloodSections categorized={wifeBlood} />

      {/* USG */}
      {hasUsgs && (
        <>
          <SubHead>USG Baseline Scan</SubHead>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: '10%' }}>Date</th>
                <th style={{ ...S.th, width: '12%' }}>Uterus</th>
                <th style={{ ...S.th, width: '12%' }}>Tubes</th>
                <th style={{ ...S.th, width: '15%' }}>Rt. Ovary (Size / AFC)</th>
                <th style={{ ...S.th, width: '15%' }}>Lt. Ovary (Size / AFC)</th>
                <th style={{ ...S.th, width: '12%' }}>Endometrium</th>
                <th style={S.th}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {storedProcedureUsgs.map((u, i) => (
                <tr key={u.id || i}>
                  <td style={S.td}>{fmt(u.scan_date)}</td>
                  <td style={S.td}>{fv(u.uterus)}</td>
                  <td style={S.td}>{fv(u.tubes)}</td>
                  <td style={S.td}>{fv(u.right_ovary_size)} / {fv(u.right_ovary)}</td>
                  <td style={S.td}>{fv(u.left_ovary_size)} / {fv(u.left_ovary)}</td>
                  <td style={S.td}>{fv(u.endometrium_size)}</td>
                  <td style={S.td} dangerouslySetInnerHTML={{ __html: u.remarks || '—' }} />
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ══════════════ HUSBAND ══════════════ */}
      {hasHusband && (
        <>
          <SecHead bg="#2a2a2a">♂ &nbsp;Husband — {fv(spouseInfo?.full_name)}</SecHead>

          <SubHead>Vitals — Initiation Day</SubHead>
          <VitalsTable vitals={husbandVitals} />

          <MedicalBlock genericHistory={husbandGenericHistory} surgicalHistory={husbandSurgicalHistory} />

          {/* Semen Analysis */}
          <SubHead>Semen Analysis</SubHead>
          {semenDates.length > 0 ? semenDates.map(date => {
            const items = [...semenByDate[date]].sort((a, b) => {
              const ia = a.param_group_member_option_id || a.irm_param_group_member_options_id || 0;
              const ib = b.param_group_member_option_id || b.irm_param_group_member_options_id || 0;
              return ia - ib;
            });
            return (
              <React.Fragment key={date}>
                <MiniHead>Date: {fmt(date)}</MiniHead>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width: '35%' }}>Parameter</th>
                      <th style={{ ...S.th, width: '25%' }}>Value</th>
                      <th style={S.th}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((h, i) => (
                      <tr key={h.id || i}>
                        <td style={S.td}>{fv(h.param_group_member_option?.value)}</td>
                        <td style={S.td} dangerouslySetInnerHTML={{ __html: h.value || '—' }} />
                        <td style={S.td} dangerouslySetInnerHTML={{ __html: h.remarks || '—' }} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </React.Fragment>
            );
          }) : (
            <table style={S.table}><tbody><NoDataRow cols={3} /></tbody></table>
          )}

          {/* Husband Blood Tests */}
          <SubHead>Blood Tests</SubHead>
          <BloodSections categorized={husbandBlood} />
        </>
      )}

      {/* ══════════════ DONOR FEMALE ══════════════ */}
      {donorFemaleInfo && (
        <>
          <SecHead bg="#3a3a3a">♀ &nbsp;Donor Female — {fv([donorFemaleInfo?.first_name, donorFemaleInfo?.middle_name, donorFemaleInfo?.last_name].filter(Boolean).join(' ') || donorFemaleInfo?.full_name)}</SecHead>

          <SubHead>Basic Information</SubHead>
          <DonorInfoTable donorInfo={donorFemaleInfo} donorType="F" />

          <MedicalBlock genericHistory={donorFemaleGenericHistory} surgicalHistory={donorFemaleSurgicalHistory} />

          {/* Donor Female Blood Tests */}
          <SubHead>Blood Tests</SubHead>
          <BloodSections categorized={donorFemaleBlood} />
        </>
      )}

      {/* ══════════════ DONOR MALE ══════════════ */}
      {donorMaleInfo && (
        <>
          <SecHead bg="#4a4a4a">♂ &nbsp;Donor Male — {fv([donorMaleInfo?.first_name, donorMaleInfo?.middle_name, donorMaleInfo?.last_name].filter(Boolean).join(' ') || donorMaleInfo?.full_name)}</SecHead>

          <SubHead>Basic Information</SubHead>
          <DonorInfoTable donorInfo={donorMaleInfo} donorType="M" />

          <MedicalBlock genericHistory={donorMaleGenericHistory} surgicalHistory={donorMaleSurgicalHistory} />

          {/* Donor Male Semen Analysis */}
          <SubHead>Semen Analysis</SubHead>
          {donorMaleSemenAnalysis?.length > 0 ? (() => {
            const semenByDate = {};
            donorMaleSemenAnalysis.forEach(h => {
              const d = h.history_date || 'Unknown';
              if (!semenByDate[d]) semenByDate[d] = [];
              semenByDate[d].push(h);
            });
            const semenDates = Object.keys(semenByDate).sort((a, b) => new Date(b) - new Date(a));
            return semenDates.map(date => {
              const items = [...semenByDate[date]].sort((a, b) => {
                const ia = a.param_group_member_option_id || a.irm_param_group_member_options_id || 0;
                const ib = b.param_group_member_option_id || b.irm_param_group_member_options_id || 0;
                return ia - ib;
              });
              return (
                <React.Fragment key={date}>
                  <MiniHead>Date: {fmt(date)}</MiniHead>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={{ ...S.th, width: '35%' }}>Parameter</th>
                        <th style={{ ...S.th, width: '25%' }}>Value</th>
                        <th style={S.th}>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((h, i) => (
                        <tr key={h.id || i}>
                          <td style={S.td}>{fv(h.param_group_member_option?.value)}</td>
                          <td style={S.td} dangerouslySetInnerHTML={{ __html: h.value || '—' }} />
                          <td style={S.td} dangerouslySetInnerHTML={{ __html: h.remarks || '—' }} />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </React.Fragment>
              );
            });
          })() : (
            <table style={S.table}><tbody><NoDataRow cols={3} /></tbody></table>
          )}

          {/* Donor Male Blood Tests */}
          <SubHead>Blood Tests</SubHead>
          <BloodSections categorized={donorMaleBlood} />
        </>
      )}

      {/* ══════════════ PLAN & CAUSE ══════════════ */}
      {hasPlan && (
        <>
          <SecHead bg="#333">Plan &amp; Cause of Infertility</SecHead>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: '50%' }}>Cause of Infertility</th>
                <th style={{ ...S.th, width: '50%' }}>Plan</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...S.td, lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{ __html: procedureDetail.cause_of_infertility || '—' }} />
                <td style={{ ...S.td, lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{ __html: procedureDetail.plan || '—' }} />
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* ─── Footer ─────────────────────────────── */}
      <div style={S.footer}>
        <div>HB 36/A/3, Sector III, Salt Lake, Kolkata – 700106 &nbsp;☎&nbsp; 033-2321-5125/27</div>
        <div>DD 18/5/1, Sector I, Salt Lake, Kolkata – 700064 &nbsp;☎&nbsp; 033-2334-1547</div>
        <div>Emergency: 8420920232 &nbsp;|&nbsp; bncirm@gmail.com &nbsp;|&nbsp; www.irmkolkata.org</div>
      </div>

    </div>
  );
});

ProcedureInitiationPrint.displayName = 'ProcedureInitiationPrint';

ProcedureInitiationPrint.propTypes = {
  patientInfo:              PropTypes.object,
  spouseInfo:               PropTypes.object,
  procedureData:            PropTypes.object,
  obstetricHistory:         PropTypes.object,
  miscarriageHistories:     PropTypes.array,
  menstrualHistories:       PropTypes.array,
  genericHistory:           PropTypes.object,
  surgicalHistory:          PropTypes.array,
  bloodGroup:               PropTypes.string,
  procedureDetail:          PropTypes.object,
  husbandGenericHistory:    PropTypes.object,
  husbandSurgicalHistory:   PropTypes.array,
  husbandSemenAnalysis:     PropTypes.array,
  husbandContactHistories:  PropTypes.array,
  storedProcedureHistories: PropTypes.array,
  bloodParams:              PropTypes.object,
  storedProcedureUsgs:      PropTypes.array,
  wifeVitals:               PropTypes.object,
  husbandVitals:            PropTypes.object,
  donorFemaleInfo:          PropTypes.object,
  donorFemaleGenericHistory: PropTypes.object,
  donorFemaleSurgicalHistory: PropTypes.array,
  donorFemaleContactHistories: PropTypes.array,
  donorMaleInfo:            PropTypes.object,
  donorMaleGenericHistory:  PropTypes.object,
  donorMaleSurgicalHistory: PropTypes.array,
  donorMaleContactHistories: PropTypes.array,
  donorMaleSemenAnalysis:   PropTypes.array,
};

export default ProcedureInitiationPrint;
